import Carousel, { type CarouselOptions } from 'components/emblaCarousel';
import { type ReactNode } from 'react';

export type HeroImageCarouselSlideProps = {
  imagePosition: 'left' | 'right';
  colorTheme: 'gold-lighter' | 'gray-dark';
  preHeadline: ReactNode;
  headline: ReactNode;
  description: ReactNode;
  links: ReactNode;
  image: ReactNode;
};

type HeroImageCarouselProps = {
  slides: HeroImageCarouselSlideProps[];
};

function HeroImageCarouselSlide(props: HeroImageCarouselSlideProps) {
  const { preHeadline, headline, description, links, colorTheme, imagePosition, image } = props;

  const imagePositionClass = imagePosition === 'left' ? 'order-1' : 'order-2';
  const calloutPositionClass = imagePosition === 'left' ? 'order-2' : 'order-1';
  const colorThemeClass = colorTheme === 'gold-lighter' ? 'hero-carousel-slide--gold' : 'hero-carousel-slide--gray';

  return (
    <div className={`sm:row hero-carousel-slide flex flex-grow flex-col gap-x-0 ${colorThemeClass}`}>
      <div
        className={`order-2 flex flex-col justify-center px-24 py-32 xl:px-56 md:${calloutPositionClass} col-span-12 md:col-span-5`}
      >
        <h1 className="before-headline font-condensed text-base uppercase leading-5">{preHeadline}</h1>
        <h2 className="headline md:leading-56 my-16 font-display text-[31.6px] sm:text-[40px] sm:leading-[48px] md:text-[47px]">
          {headline}
        </h2>
        <p className="description leading-24 text-base">{description}</p>

        <div className="flex justify-between">{links}</div>
      </div>

      <div
        className={`portrait:max-h-none md:max-h-none order-1 max-h-[48vh] p-0 md:${imagePositionClass} col-span-12 md:col-span-7`}
      >
        {image}
      </div>
    </div>
  );
}

const defaultCarouselOptions: CarouselOptions = {
  slidesToScroll: 1,
  loop: true,
  autoplay: {
    delay: 6000,
    stopOnInteraction: false,
  },
} as const;

export default function HeroImageCarousel({ slides }: HeroImageCarouselProps) {
  return (
    <Carousel autoplay showPagination options={defaultCarouselOptions} className="hero-carousel flex">
      {slides.map((slide, index) => (
        <div key={`slide-${index + 1}`} className="flex" style={{ flex: '0 0 100%' }}>
          <HeroImageCarouselSlide {...slide} />
        </div>
      ))}
    </Carousel>
  );
}
